import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import {
  EmailMessage,
  EmailMessageFilter,
  EmailMessageStatus,
  EmailMessageStatusConstants,
  EmailMessageStatusDisplayNames,
} from 'src/app/common/EmailMessage';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { PagedList } from 'src/app/common/PagedList';
import { ProfileRoleConstants } from 'src/app/common/ProfileRole';
import { EmailMessageService } from 'src/app/services/email-message.service';
import { AppProfileService } from 'src/app/services/profile.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

interface StatusItem {
  value: EmailMessageStatus | null;
  label: string;
}

// Grid row: the API's ISO strings converted once so Kendo's format="{0:g}" applies.
interface EmailMessageRow extends EmailMessage {
  createdAtDate: Date;
  sentAtDate: Date | null;
  nextAttemptAtDate: Date | null;
}

/**
 * Backend #657 slice 3: the Carrier admin's view of the e-mail send queue („E-Mail-Warteschlange")
 * with a retry for Failed rows. Server-paged (#648): take = the user's ListPageSize, sort
 * whitelist mirrors EmailMessageSortFields. Carrier-admin only — the BFF answers 403 for anyone
 * else, and the page refuses to load for non-carrier / non-admin profiles.
 */
@Component({
  imports: [CommonModule, FormsModule, KENDO_GRID, KENDO_DROPDOWNS, FontAwesomeModule, AsyncClickDirective, GridNoRecordsComponent],
  selector: 'app-email-queue',
  templateUrl: './email-queue.component.html',
  styleUrls: ['./email-queue.component.css'],
})
export class EmailQueueComponent implements OnInit {
  private emailMessageService = inject(EmailMessageService);
  private profileService = inject(AppProfileService);
  private toastr = inject(ToastrService);

  private destroyRef = inject(DestroyRef);

  public faRedo = faRedo;
  public profile: ExtendedProfile | null = null;
  public isAdmin = false;

  // #648: server-side paging/sorting; default order = newest first. The map is the server's
  // whitelist (EmailMessageSortFields) — every other column is [sortable]="false".
  public list = new PagedList<EmailMessageRow>({
    sortFields: {
      createdAt: 'createdAt',
      status: 'status',
      recipientEmail: 'recipientEmail',
      templateKey: 'templateKey',
      attemptCount: 'attemptCount',
      sentAt: 'sentAt',
      completedAt: 'completedAt',
    },
    defaultSort: [{ field: 'createdAt', dir: 'desc' }],
    errorMessage: $localize`Die E-Mail-Warteschlange konnte nicht geladen werden.`,
    // Carrier admins only — the BFF answers 403 for anyone else.
    fetch: (paging) =>
      this.profile?.isCarrier && this.isAdmin
        ? this.emailMessageService
            .getByFilter(this.filter, paging)
            .pipe(map((r) => ({ items: r.items.map((m) => this.toRow(m)), totalCount: r.totalCount })))
        : null,
  });

  public filter: EmailMessageFilter = { status: null, recipient: '', templateKey: '', correlationId: '' };

  public readonly allStatusesLabel = $localize`Alle Status`;
  public readonly statusItems: StatusItem[] = Object.values(EmailMessageStatusConstants).map((value) => ({
    value,
    label: EmailMessageStatusDisplayNames[value],
  }));

  public ngOnInit() {
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      this.isAdmin = p?.role == ProfileRoleConstants.RoleIdAdmin;
      this.list.load();
    });
  }

  public onStatusChange(status: EmailMessageStatus | null) {
    this.filter.status = status;
    this.search();
  }

  // Any filter change starts over at page 1 — the total changed.
  public search() {
    this.list.search();
  }

  public reload() {
    this.list.load();
  }

  public statusName(status: string): string {
    return EmailMessageStatusDisplayNames[status] ?? status;
  }

  public statusClass(status: string): string {
    switch (status) {
      case EmailMessageStatusConstants.Sent:
        return 'bg-success';
      case EmailMessageStatusConstants.Failed:
        return 'bg-danger';
      case EmailMessageStatusConstants.Sending:
        return 'bg-primary';
      case EmailMessageStatusConstants.Pending:
        return 'bg-warning text-dark';
      default:
        return 'bg-secondary';
    }
  }

  // Only a Failed row can be retried: a Sent mail must never go out twice and a Pending one
  // is already on its way (the server answers 409 for both — the button is just the hint).
  public canRetry(row: EmailMessage): boolean {
    return row.status === EmailMessageStatusConstants.Failed;
  }

  // [asyncClick] factory (CLAUDE.md mutation-button rule): returns the cold request; the
  // directive subscribes on click and disables the button until it settles. Status-
  // discriminating toasts, so this bypasses callWithToasts$ like scan-provider trigger-now.
  public onRetry(row: EmailMessageRow): () => Observable<unknown> {
    return () =>
      this.emailMessageService.retry(row.id).pipe(
        tap({
          next: (updated) => {
            this.toastr.success($localize`E-Mail wurde erneut in die Warteschlange gestellt.`);
            Object.assign(row, this.toRow(updated));
            this.refreshGrid();
          },
          error: (err) => {
            if (err?.status === 409) {
              this.toastr.info(
                $localize`Diese E-Mail ist nicht fehlgeschlagen — sie wird gesendet oder wurde bereits gesendet.`
              );
              this.list.load();
            } else if (err?.status === 404) {
              this.toastr.warning($localize`Diese E-Mail existiert nicht mehr (Aufbewahrungsfrist abgelaufen).`);
              this.list.load();
            } else {
              this.toastr.error($localize`Beim erneuten Senden der E-Mail ist ein Fehler aufgetreten.`);
            }
          },
        })
      );
  }

  private refreshGrid() {
    this.list.gridData = { data: [...(this.list.gridData.data as EmailMessageRow[])], total: this.list.gridData.total };
  }

  private toRow(m: EmailMessage): EmailMessageRow {
    return {
      ...m,
      createdAtDate: new Date(m.createdAt),
      sentAtDate: m.sentAt ? new Date(m.sentAt) : null,
      nextAttemptAtDate: m.nextAttemptAt ? new Date(m.nextAttemptAt) : null,
    };
  }
}
